import { createQueryController } from '@tanstack/lit-query';
import { trackStarterShown } from 'Analytics/track-starter-shown';
import {
    fetchChatConfiguration,
    fetchPersonalizedStarterQuestions,
    type ChatConfiguration,
    type PersonalizedStarterQuestionsResponse,
    type PredefinedQuestion,
} from 'Api/chatConfiguration';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import 'Askmedi/components/vm-prompt-input/vm-prompt-input';
import 'Askmedi/components/vm-suggested-questions/vm-suggested-questions';
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { queryClient } from '../../../../query-client';
import Styles from './vm-askmedi-hero.scss';

export type HeroQuestionSource = 'predefined' | 'manual';

interface HeroSendDetail {
    message: string;
    source: HeroQuestionSource;
}

/**
 * AskMedi homepage hero — heading, prompt input, and predefined question
 * suggestions. Used both inside the AskMedi React app and on the main
 * homefeed.html template.
 *
 * Fires `vm-send` whenever the user submits a question, either by typing or by
 * clicking a suggestion.
 *
 * @fires vm-send - CustomEvent<{ message: string; source: 'predefined' | 'manual' }>
 */
@customElement('vm-askmedi-hero')
export class VmAskmediHero extends LitElement {
    static override styles = Styles;

    /** Auto-focus the prompt input on mount. */
    @property({ type: Boolean, attribute: 'auto-focus' })
    autoFocusOnMount = false;

    /** Hide the disclaimer beneath the prompt input. */
    @property({ type: Boolean, attribute: 'hide-disclaimer' })
    hideDisclaimer = false;

    /** Cap on suggested questions rendered. `0` (default) means show all. */
    @property({ type: Number, attribute: 'max-questions' })
    maxQuestions = 0;

    private _configQuery = createQueryController<ChatConfiguration>(
        this,
        {
            queryKey: ['chatConfiguration', 'askmedi'],
            queryFn: () => fetchChatConfiguration({ contentType: 'askmedi' }),
        },
        queryClient,
    );

    private _starterQuery = createQueryController<PersonalizedStarterQuestionsResponse>(
        this,
        {
            queryKey: ['personalizedStarterQuestions'],
            queryFn: () => fetchPersonalizedStarterQuestions(),
        },
        queryClient,
    );

    private _impressionsFired = new Set<number>();

    /** Personalized starters if the async call returned any; otherwise the static baseline. */
    private _resolvedStarters(): PredefinedQuestion[] {
        const personalized = this._starterQuery.current.data?.questions ?? [];
        if (personalized.length > 0) {
            return personalized;
        }
        return this._configQuery.current.data?.predefined_questions ?? [];
    }

    override updated() {
        if (this._starterQuery.current.isLoading) return;
        // Dedupe scope is per-mount — a fresh mount (user returns to the
        // homepage) re-fires intentionally: each visit is its own impression.
        const all = this._resolvedStarters();
        const visible = this.maxQuestions > 0 ? all.slice(0, this.maxQuestions) : all;
        visible.forEach((q, position) => {
            if (this._impressionsFired.has(q.id)) return;
            this._impressionsFired.add(q.id);
            trackStarterShown(q, position);
        });
    }

    private _emitSend(message: string, source: HeroQuestionSource) {
        this.dispatchEvent(
            new CustomEvent<HeroSendDetail>('vm-send', {
                detail: { message, source },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private _handlePromptSend = (e: CustomEvent<{ message: string }>) => {
        // Inner vm-send bubbles+composes; stop it so it doesn't double-fire alongside our re-emit.
        e.stopPropagation();
        this._emitSend(e.detail.message, 'manual');
    };

    private _handleSuggestionSelect = (e: CustomEvent<{ question: string }>) => {
        e.stopPropagation();
        this._emitSend(e.detail.question, 'predefined');
    };

    override render() {
        const isLoadingStarters = this._starterQuery.current.isLoading;
        const allQuestions = this._resolvedStarters().map((q) => q.question);
        const questions = this.maxQuestions > 0 ? allQuestions.slice(0, this.maxQuestions) : allQuestions;

        return html`
            <div class="content">
                <h1 class="heading">
                    <vm-icon-next class="heading__sparkle" name="sparkle" size="lg"></vm-icon-next>
                    Ask a Clinical Question
                </h1>

                <p class="subtitle">
                    Consult 100,000+ videos and the latest guidelines for real-world expert perspectives.
                </p>

                <div class="composer">
                    <vm-prompt-input
                        ?auto-focus=${this.autoFocusOnMount}
                        ?hide-disclaimer=${this.hideDisclaimer}
                        @vm-send=${this._handlePromptSend}
                    ></vm-prompt-input>
                </div>

                <vm-suggested-questions
                    .questions=${questions}
                    variant="minimal"
                    ?loading=${isLoadingStarters}
                    skeleton-count=${this.maxQuestions > 0 ? this.maxQuestions : 5}
                    @vm-select=${this._handleSuggestionSelect}
                ></vm-suggested-questions>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-askmedi-hero': VmAskmediHero;
    }
}
